import { useEffect, useState } from "react";
import { getLeadsCached, deleteLead, updateLead } from "../../api/lead";
import { PipelineFilters } from "./Filter";
import { PipelineBoard } from "./PipelineBoard";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { apiCache, cacheKeys, invalidateCache } from "../../lib/cache";
import * as documentsAPI from "../../api/documents";
import api from "../../api";
import { Upload, FileText, Download, Trash2 as TrashIcon, Eye as EyeIcon } from "lucide-react";

export default function PipelinePage() {
  const navigate = useNavigate();
  const [leadsList, setLeadsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSource, setSelectedSource] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedSettlementDate, setSelectedSettlementDate] = useState("all");
  const [totalLeads, setTotalLeads] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLead, setViewLead] = useState(null);

  // Fetch leads with dynamic filters using cache
  const fetchLeads = async (filters = {}, forceRefresh = false) => {
    try {
      const params = {
        page_size: 100, // Fetch 100 records at once
        ...filters
      };

      // Clean up filter parameters
      if (params.search === "") delete params.search;
      if (params.source === "all") delete params.source;
      if (params.priority === "all") delete params.priority;
      if (params.stage === "all") delete params.stage;
      if (params.settlement_date === "all") delete params.settlement_date;

      // Only show loading spinner if no cache exists or force refresh
      const cacheKey = cacheKeys.leads(params);
      if (!apiCache.has(cacheKey) || forceRefresh) {
        setLoading(true);
      }

      // Use cached API call with stale-while-revalidate
      const leadsRes = await getLeadsCached(params, { forceRefresh });

      setLeadsList(leadsRes.data?.results || []);
      setTotalLeads(leadsRes.data?.count || 0);
      setError(null);
    } catch (err) {
      setLeadsList([]);
      setError("Failed to load leads.");
      console.error("Error fetching leads:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load - fetch all leads once
  useEffect(() => {
    fetchLeads();
  }, []);

  // Handle filter changes with debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const filters = {};

      if (searchTerm) filters.search = searchTerm;
      if (selectedSource !== "all") filters.source = selectedSource;
      if (selectedPriority !== "all") filters.priority = selectedPriority;
      if (selectedStatus !== "all") filters.stage = selectedStatus;
      if (selectedSettlementDate !== "all") filters.settlement_date = selectedSettlementDate;

      fetchLeads(filters);
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedSource, selectedPriority, selectedStatus, selectedSettlementDate]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'search':
        setSearchTerm(value);
        break;
      case 'source':
        setSelectedSource(value);
        break;
      case 'priority':
        setSelectedPriority(value);
        break;
      case 'status':
        setSelectedStatus(value);
        break;
      case 'settlement_date':
        setSelectedSettlementDate(value);
        break;
      default:
        break;
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedSource("all");
    setSelectedPriority("all");
    setSelectedStatus("all");
    setSelectedSettlementDate("all");
    fetchLeads(); // Fetch all leads without filters
  };

  // Handle view lead - navigate to leads page with specific lead details
  const handleViewLead = (lead) => {
    setViewLead(lead);
    setViewModalOpen(true);
  };

  // Handle edit lead
  const handleEditLead = (lead) => {
    setEditLead(lead);
    setEditModalOpen(true);
  };

  // Handle delete lead
  const handleDeleteLead = async (lead) => {
    if (!window.confirm(`Are you sure you want to delete the lead for ${lead.full_name || 'this contact'}?`)) {
      return;
    }

    setDeletingId(lead.id);

    // Optimistic update: remove from UI immediately
    const previousLeads = [...leadsList];
    setLeadsList(leadsList.filter(l => l.id !== lead.id));
    setTotalLeads(prev => prev - 1);

    try {
      await deleteLead(lead.id);

      toast.success("Lead deleted successfully!");

      // Cache is already invalidated by deleteLead API
      // Refetch to ensure consistency
      const filters = buildCurrentFilters();
      fetchLeads(filters, true); // Force refresh
    } catch (err) {
      // Rollback optimistic update on error
      setLeadsList(previousLeads);
      setTotalLeads(prev => prev + 1);

      toast.error("Failed to delete lead");
      console.error("Delete error:", err);
    } finally {
      setDeletingId(null);
    }
  };

  // Helper to build current filter object
  const buildCurrentFilters = () => {
    const filters = {};
    if (searchTerm) filters.search = searchTerm;
    if (selectedSource !== "all") filters.source = selectedSource;
    if (selectedPriority !== "all") filters.priority = selectedPriority;
    if (selectedStatus !== "all") filters.stage = selectedStatus;
    if (selectedSettlementDate !== "all") filters.settlement_date = selectedSettlementDate;
    return filters;
  };

  // Close view modal
  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewLead(null);
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditLead(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Lead Pipeline</h1>
          <p className="text-slate-600">Manage and track your leads through the sales process</p>
        </div>

        <PipelineFilters
          searchTerm={searchTerm}
          selectedSource={selectedSource}
          selectedPriority={selectedPriority}
          selectedStatus={selectedStatus}
          selectedSettlementDate={selectedSettlementDate}
          onFilterChange={handleFilterChange}
          onClearFilters={clearAllFilters}
          totalLeads={totalLeads}
          filteredLeads={leadsList.length}
        />

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        ) : (
          <PipelineBoard
            leads={leadsList}
            totalLeads={leadsList.length}
            onLeadClick={handleViewLead}
            onLeadEdit={handleEditLead}
            onLeadDelete={handleDeleteLead}
          />
        )}
      </main>

      {/* View Lead Modal */}
      {viewModalOpen && viewLead && (
        <ViewLeadModal
          lead={viewLead}
          onClose={closeViewModal}
          onEdit={() => {
            closeViewModal();
            handleEditLead(viewLead);
          }}
        />
      )}

      {/* Edit Lead Modal */}
      {editModalOpen && editLead && (
        <EditLeadModal
          lead={editLead}
          onClose={closeEditModal}
          onSubmit={async (updatedData) => {
            try {
              const payload = {
                full_name: updatedData.fullName,
                contact: updatedData.phoneNumber,
                email: updatedData.email,
                property_address: updatedData.propertyAddress,
                source: updatedData.leadSource,
                priority: updatedData.priority,
                timeline: updatedData.timeline || "New",
                initial_notes: updatedData.initialNotes,
                agent: updatedData.agent || updatedData.agentId,
                stage: updatedData.stage,
                price_range: updatedData.price_range,
              };

              // Only add numeric/date fields if they have valid values
              if (updatedData.commission && updatedData.commission.toString().trim() !== "") {
                payload.commission = updatedData.commission;
              }
              if (updatedData.gained_commission && updatedData.gained_commission.toString().trim() !== "") {
                payload.gained_commission = updatedData.gained_commission;
              }
              if (updatedData.settlement_date && updatedData.settlement_date.toString().trim() !== "") {
                payload.settlement_date = updatedData.settlement_date;
              }

              // Optimistic update: update UI immediately
              setLeadsList(leadsList.map(lead =>
                lead.id === editLead.id
                  ? { ...lead, ...payload, full_name: payload.full_name, contact: payload.contact }
                  : lead
              ));

              await updateLead(editLead.id, payload);

              toast.success("Lead updated successfully!");
              closeEditModal();

              // Cache is already invalidated by updateLead API
              // Refetch to ensure consistency
              const filters = buildCurrentFilters();
              fetchLeads(filters, true); // Force refresh
            } catch (err) {
              toast.error("Failed to update lead");
              console.error("Update error:", err);

              // Refetch on error to restore correct state
              const filters = buildCurrentFilters();
              fetchLeads(filters, true);
            }
          }}
        />
      )}
    </div>
  )
}

// View Lead Modal Component
function ViewLeadModal({ lead, onClose, onEdit }) {
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [transcription, setTranscription] = useState([]);
  const [loadingTranscription, setLoadingTranscription] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
    fetchTranscription();
  }, [lead.id]);

  const fetchTranscription = async () => {
    try {
      setLoadingTranscription(true);
      const response = await api.get("api/ai-voice-caller/agent-calls/");
      const allCalls = response.data || [];

      const leadCalls = allCalls.filter(call => String(call.lead) === String(lead.id))
        .sort((a, b) => new Date(b.created_at || b.start_datetime) - new Date(a.created_at || a.start_datetime));

      if (leadCalls.length > 0) {
        setTranscription(leadCalls[0].conversation_history || []);
      } else {
        setTranscription([]);
      }
    } catch (error) {
      console.error('Error fetching transcription:', error);
      setTranscription([]);
    } finally {
      setLoadingTranscription(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoadingDocs(true);
      const response = await documentsAPI.getDocuments({ related_lead: lead.id });
      setDocuments(Array.isArray(response) ? response : response.results || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', selectedFile.name);
      formData.append('description', `Document for lead: ${lead.full_name}`);
      formData.append('category', 'miscellaneous');
      formData.append('related_lead', lead.id);

      await documentsAPI.uploadDocument(formData);
      toast.success('Document uploaded successfully!');
      setSelectedFile(null);
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const blob = await documentsAPI.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Document downloaded');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await documentsAPI.deleteDocument(docId);
      toast.success('Document deleted successfully!');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Lead Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Full Name</label>
                <p className="text-base text-gray-900 mt-1">{lead.full_name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Email</label>
                <p className="text-base text-gray-900 mt-1">{lead.email || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Phone</label>
                <p className="text-base text-gray-900 mt-1">{lead.contact || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Property Address</label>
                <p className="text-base text-gray-900 mt-1">{lead.property_address || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Lead Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Lead Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Source</label>
                <p className="text-base text-gray-900 mt-1">{lead.source || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Priority</label>
                <p className="text-base text-gray-900 mt-1">{lead.priority || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Stage</label>
                <p className="text-base text-gray-900 mt-1">{lead.stage || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Timeline</label>
                <p className="text-base text-gray-900 mt-1">{lead.timeline || 'N/A'}</p>
              </div>
              {lead.price_range_text && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Price Range</label>
                  <p className="text-base text-gray-900 mt-1">{lead.price_range_text}</p>
                </div>
              )}
              {lead.commission && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Commission</label>
                  <p className="text-base text-gray-900 mt-1">${parseFloat(lead.commission).toLocaleString()}</p>
                </div>
              )}
              {lead.gained_commission && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Gained Commission</label>
                  <p className="text-base text-gray-900 mt-1">${parseFloat(lead.gained_commission).toLocaleString()}</p>
                </div>
              )}
              {lead.settlement_date && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Settlement Date</label>
                  <p className="text-base text-gray-900 mt-1">{new Date(lead.settlement_date).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {lead.initial_notes && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
              <p className="text-base text-gray-700 bg-gray-50 p-4 rounded-lg">{lead.initial_notes}</p>
            </div>
          )}

          {/* Transcription Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Transcription</h3>
            {loadingTranscription ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#55C5D0]"></div>
              </div>
            ) : transcription && transcription.length > 0 ? (
              <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                {transcription.map((entry, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 ${entry.role === "user" ? "justify-start" : "justify-end"}`}
                  >
                    <div
                      className={`p-3 rounded-lg max-w-[80%] text-sm ${entry.role === "user"
                        ? "bg-blue-50 text-gray-800"
                        : "bg-green-50 text-gray-800"
                        }`}
                    >
                      <p className="text-xs font-bold mb-1 opacity-50">
                        {entry.role === "user" ? "Customer" : "AI Agent"}
                      </p>
                      {entry.content}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm italic">No transcription available</p>
            )}
          </div>

          {/* Documents Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Documents
            </h3>

            {/* Upload Section */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#55C5D0] file:text-white hover:file:bg-[#45b5c0]"
                />
                <button
                  onClick={handleUploadDocument}
                  disabled={!selectedFile || uploading}
                  className="px-4 py-2 bg-[#55C5D0] text-white rounded-lg hover:bg-[#45b5c0] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>

            {/* Documents List */}
            {loadingDocs ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#55C5D0] mx-auto"></div>
              </div>
            ) : documents.length > 0 ? (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {doc.size ? `${(doc.size / 1024).toFixed(2)} KB` : 'Unknown size'} •
                          {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Unknown date'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDownloadDocument(doc)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No documents uploaded yet</p>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="px-4 py-2 text-white bg-[#55C5D0] rounded-lg hover:bg-[#45b5c0] transition-colors"
          >
            Edit Lead
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Lead Modal Component
function EditLeadModal({ lead, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    fullName: lead?.full_name || "",
    phoneNumber: lead?.contact || "",
    email: lead?.email || "",
    propertyAddress: lead?.property_address || "",
    leadSource: lead?.source || "",
    timeline: lead?.timeline || "",
    priority: lead?.priority || "Medium",
    stage: lead?.stage || "Unassessed",
    initialNotes: lead?.initial_notes || "",
    agent: lead?.agent || "",
    commission: lead?.commission || "",
    gained_commission: lead?.gained_commission || "",
    settlement_date: lead?.settlement_date || "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, [lead.id]);

  const fetchDocuments = async () => {
    try {
      setLoadingDocs(true);
      const response = await documentsAPI.getDocuments({ related_lead: lead.id });
      setDocuments(Array.isArray(response) ? response : response.results || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formDataDoc = new FormData();
      formDataDoc.append('file', selectedFile);
      formDataDoc.append('name', selectedFile.name);
      formDataDoc.append('description', `Document for lead: ${lead.full_name}`);
      formDataDoc.append('category', 'miscellaneous');
      formDataDoc.append('related_lead', lead.id);

      await documentsAPI.uploadDocument(formDataDoc);
      toast.success('Document uploaded successfully!');
      setSelectedFile(null);
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const blob = await documentsAPI.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Document downloaded');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await documentsAPI.deleteDocument(docId);
      toast.success('Document deleted successfully!');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.fullName.trim()) errors.fullName = "Full name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.propertyAddress.trim()) errors.propertyAddress = "Property address is required";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const allStages = [
    "Unassessed",
    "New Lead",
    "Attempting Contact",
    "Contact - Not Qualified",
    "Contacted Nurture",
    "Booked Appraisal",
    "Appraisal Complete - Awaiting Decision",
    "Follow-Up Ongoing",
    "Won - Listing Signed",
    "Lost - Listed With Another Agent",
    "Unresponsive - Cold",
  ];

  const leadSources = [
    "realestate",
    "form",
    "direct",
    "voiceCaller",
    "chatbot",
    "referral",
    "social",
    "other",
  ];

  const priorities = ["High", "Medium", "Low"];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Edit Lead</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
                {formErrors.fullName && <p className="text-red-500 text-sm mt-1">{formErrors.fullName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
                {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
                {formErrors.propertyAddress && <p className="text-red-500 text-sm mt-1">{formErrors.propertyAddress}</p>}
              </div>
            </div>
          </div>

          {/* Lead Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
                <select
                  name="leadSource"
                  value={formData.leadSource}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                >
                  <option value="">Select source</option>
                  {leadSources.map(source => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                >
                  {priorities.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
                <select
                  name="stage"
                  value={formData.stage}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                >
                  {allStages.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timeline</label>
                <input
                  type="text"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commission</label>
                <input
                  type="number"
                  name="commission"
                  value={formData.commission}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gained Commission</label>
                <input
                  type="number"
                  name="gained_commission"
                  value={formData.gained_commission}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Date</label>
                <input
                  type="date"
                  name="settlement_date"
                  value={formData.settlement_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
            <textarea
              name="initialNotes"
              value={formData.initialNotes}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
              placeholder="Add any additional notes..."
            />
          </div>

          {/* Documents Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Documents
            </h3>

            {/* Upload Section */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#55C5D0] file:text-white hover:file:bg-[#45b5c0]"
                />
                <button
                  type="button"
                  onClick={handleUploadDocument}
                  disabled={!selectedFile || uploading}
                  className="px-4 py-2 bg-[#55C5D0] text-white rounded-lg hover:bg-[#45b5c0] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>

            {/* Documents List */}
            {loadingDocs ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#55C5D0] mx-auto"></div>
              </div>
            ) : documents.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {doc.size ? `${(doc.size / 1024).toFixed(2)} KB` : 'Unknown size'} •
                          {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Unknown date'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleDownloadDocument(doc)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No documents uploaded yet</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-white bg-[#55C5D0] rounded-lg hover:bg-[#45b5c0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
